import { useEffect, useMemo, useRef, type RefObject } from 'react';

import { Canvas, type CanvasRef } from '../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../core/components/canvas/types/canvas-shape.js';
import { DIM_CANVAS_ALPHA } from '../constants.js';
import { useDrawSankey } from '../hooks/use-draw-sankey.js';
import { useSankeyActiveHighlight } from '../store/selectors.js';
import type { SankeyLayoutModel } from '../types/sankey-internal.js';
import { canvasContainer, highlightCanvas } from './SankeyCanvas.sty.js';

interface SankeyDrawProps {
  layout: SankeyLayoutModel;
  nodeColors: Map<string, string>;
  overlayCanvasRef: RefObject<HTMLCanvasElement | null>;
}

function SankeyDraw({
  layout,
  nodeColors,
  overlayCanvasRef,
}: Readonly<SankeyDrawProps>) {
  useDrawSankey(layout, nodeColors, overlayCanvasRef);
  return null;
}

SankeyDraw['displayName'] = 'SankeyDraw';

interface SankeyCanvasProps {
  nodeColors: Map<string, string>;
  layout: SankeyLayoutModel;
  width: number;
  height: number;
}

export function SankeyCanvas({
  nodeColors,
  layout,
  width,
  height,
}: Readonly<SankeyCanvasProps>) {
  const shapeRefs = useRef(new Map<string, RefObject<CanvasShape>>());
  const canvasContextValue = useMemo(
    () => ({ shapeRefs: shapeRefs.current }),
    [],
  );
  const mainCanvasRef = useRef<CanvasRef>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const activeSet = useSankeyActiveHighlight();

  useEffect(() => {
    const overlayCanvas = overlayCanvasRef.current;
    if (!overlayCanvas || width <= 0 || height <= 0) {
      return;
    }
    const dpr = Math.max(1, window.devicePixelRatio) || 1;
    overlayCanvas.width = width * dpr;
    overlayCanvas.height = height * dpr;
    const ctx = overlayCanvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
    overlayCanvas.style.width = '100%';
    overlayCanvas.style.height = '100%';
  }, [width, height]);

  useEffect(() => {
    const opacity = activeSet ? DIM_CANVAS_ALPHA : 1;
    mainCanvasRef.current?.customSetStyle('opacity', String(opacity));
  }, [activeSet]);

  return (
    <div className={canvasContainer}>
      <CanvasContext.Provider value={canvasContextValue}>
        <Canvas ref={mainCanvasRef} width={width} height={height}>
          <SankeyDraw
            layout={layout}
            nodeColors={nodeColors}
            overlayCanvasRef={overlayCanvasRef}
          />
        </Canvas>
      </CanvasContext.Provider>
      <canvas ref={overlayCanvasRef} className={highlightCanvas} />
    </div>
  );
}

SankeyCanvas['displayName'] = 'SankeyCanvas';
